import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useUi } from '@allrouted/ui/Toaster';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { Drawer } from '@allrouted/ui/Drawer';
import { Disclosure, Field, NumberInput, TextArea, TextInput, TimeInput } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { useAuth } from '../AuthGate';
import { CustomFieldsEditor, toFields, type FieldRow } from './CustomFieldsEditor';

/**
 * Creating an order from the console.
 *
 * The field set is the Configuration tab's, in the same order, because that is
 * the form a dispatcher already knows. What is missing here is a geocoder: an
 * order needs a position, nothing derives one from the address yet, so the
 * coordinates sit under Advanced starting from a depot and the drawer says so
 * rather than dropping a pin in the ocean.
 */

const UNPINNED = '__any__';

export function NewOrderDrawer({
  open,
  territoryId,
  date,
  onClose,
  onCreated,
}: {
  open: boolean;
  territoryId: string | null;
  date: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const ui = useUi();
  const [vehicles, setVehicles] = useState<{ id: string; name: string }[]>([]);
  const [near, setNear] = useState<{ name: string; lat: number; lng: number } | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [type, setType] = useState('service');
  const [address, setAddress] = useState('');
  const [serviceMinutes, setServiceMinutes] = useState(45);
  const [windowStart, setWindowStart] = useState<number | null>(8 * 3600);
  const [windowEnd, setWindowEnd] = useState<number | null>(17 * 3600);
  const [priority, setPriority] = useState(50);
  const [requiredVehicleId, setRequiredVehicleId] = useState(UNPINNED);
  const [requiredTags, setRequiredTags] = useState('');
  const [notes, setNotes] = useState('');
  const [scheduledDate, setScheduledDate] = useState(date);
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  /* Coordinates typed by hand. Only then are they sent: otherwise the address decides. */
  const [manual, setManual] = useState(false);
  const [fieldRows, setFieldRows] = useState<FieldRow[]>([]);
  const geocoding = useAuth().me.geocoding === true;

  useEffect(() => {
    if (!open || !territoryId) return;
    setScheduledDate(date);
    void Promise.all([api.vehicles(territoryId), api.depots(territoryId)]).then(
      ([{ vehicles: fleet }, { depots }]) => {
        setVehicles(fleet as { id: string; name: string }[]);
        const list = depots as { name: string; lat: number; lng: number }[];
        const hq = list.find((d) => d.name === 'HQ') ?? list[0] ?? null;
        if (hq) {
          setNear({ name: hq.name, lat: Number(hq.lat), lng: Number(hq.lng) });
          setLat(Number(hq.lat));
          setLng(Number(hq.lng));
        }
      },
    );
  }, [open, territoryId, date]);

  const create = async () => {
    if (!territoryId) return;
    const custom = toFields(fieldRows);
    if (!custom.ok) {
      ui.notify('error', 'Check the custom fields', custom.problem);
      return;
    }
    setSaving(true);
    try {
      await api.createOrder(territoryId, {
        name: name.trim(),
        externalId: externalId.trim() || null,
        type,
        address: address.trim() ? parseAddress(address) : null,
        ...(manual || !geocoding ? { location: { lat, lng } } : {}),
        serviceTime: serviceMinutes * 60,
        priority,
        timeWindows:
          windowStart !== null && windowEnd !== null
            ? [{ start: windowStart, end: windowEnd }]
            : [],
        requiredTags: requiredTags.split(',').map((t) => t.trim()).filter(Boolean),
        requiredVehicleId: requiredVehicleId === UNPINNED ? null : requiredVehicleId,
        scheduledDate,
        notes: notes.trim() || null,
        customFields: custom.fields,
      });
      ui.notify('ok', 'Order created', 'Replan the day to place it on a route.');
      reset();
      onCreated();
      onClose();
    } catch (err) {
      ui.notify(
        'error',
        'Could not create the order',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setName('');
    setExternalId('');
    setAddress('');
    setNotes('');
    setRequiredTags('');
    setRequiredVehicleId(UNPINNED);
    setManual(false);
    setFieldRows([]);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New order"
      subtitle="The work, and what constrains who can do it"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            onClick={() => void create()}
          >
            {saving ? <span className="spinner" /> : null}
            Create order
          </button>
        </>
      }
    >
      <Field label="Name">
        <TextInput value={name} onChange={setName} placeholder="WO 9001" />
      </Field>
      <Field label="Reference" hint="The ID your other systems already use">
        <TextInput value={externalId} onChange={setExternalId} placeholder="WO-9001" />
      </Field>
      <Field label="Eligibility">
        <DatePicker value={scheduledDate} today={date} onChange={setScheduledDate} />
      </Field>
      <Field label="Required vehicle" hint="Pins the order to one technician">
        <Select
          value={requiredVehicleId}
          options={[
            { value: UNPINNED, label: 'Any vehicle' },
            ...vehicles.map((v) => ({ value: v.id, label: v.name })),
          ]}
          onChange={setRequiredVehicleId}
        />
      </Field>
      <Field label="Priority">
        <Select
          value={String(priority)}
          options={[
            { value: '25', label: 'Low' },
            { value: '50', label: 'Normal' },
            { value: '75', label: 'High' },
            { value: '100', label: 'Critical' },
          ]}
          onChange={(v) => setPriority(Number(v))}
        />
      </Field>
      <Field label="Type">
        <Select
          value={type}
          options={[
            { value: 'service', label: 'Service' },
            { value: 'delivery', label: 'Delivery' },
            { value: 'pickup', label: 'Pickup' },
          ]}
          onChange={setType}
        />
      </Field>
      <Field label="Address" wide>
        <TextInput
          value={address}
          onChange={setAddress}
          placeholder="1 Example St, Cincinnati, OH 45202, United States"
        />
      </Field>
      <Field label="Service time">
        <NumberInput value={serviceMinutes} min={0} suffix="min" onChange={setServiceMinutes} />
      </Field>
      <Field label="Time window">
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <TimeInput seconds={windowStart} onChange={setWindowStart} />
          <span style={{ color: 'var(--text-3)' }}>to</span>
          <TimeInput seconds={windowEnd} onChange={setWindowEnd} />
        </span>
      </Field>
      <Field label="Required tags" hint="Comma separated">
        <TextInput value={requiredTags} onChange={setRequiredTags} placeholder="sealed system" />
      </Field>
      <Field label="Notes" wide>
        <TextArea value={notes} rows={3} onChange={setNotes} />
      </Field>

      <h3 className="section-label">Custom fields</h3>
      <CustomFieldsEditor rows={fieldRows} onChange={setFieldRows} />

      <Disclosure label="Advanced" defaultOpen={!geocoding && !near}>
        <p className="disclosure-note">
          {geocoding ? (
            manual ? (
              'These coordinates will be used instead of the address.'
            ) : (
              'The position comes from the address. Enter coordinates only to place it somewhere the address does not describe.'
            )
          ) : (
            <>
              Address lookup is not set up, so the position starts
              {near ? ` at ${near.name}` : ' at zero'} and the order is routed from here. Correct it below.
            </>
          )}
        </p>
        <Field label="Latitude">
          <NumberInput value={lat} step={0.000001} onChange={(v) => { setLat(v); setManual(true); }} />
        </Field>
        <Field label="Longitude">
          <NumberInput value={lng} step={0.000001} onChange={(v) => { setLng(v); setManual(true); }} />
        </Field>
      </Disclosure>
    </Drawer>
  );
}

/** Split a one-line address the way the depot form does. */
function parseAddress(text: string): Record<string, string | null> {
  const parts = text.split(',').map((p) => p.trim());
  const country = parts.length > 3 ? (parts.pop() ?? null) : null;
  const regionPostcode = parts.length > 2 ? (parts.pop() ?? '') : '';
  const [region = null, postcode = null] = regionPostcode.split(/\s+/);
  const city = parts.length > 1 ? (parts.pop() ?? null) : null;
  return {
    line1: parts.join(', ') || null,
    city,
    region,
    postcode,
    country,
  };
}
